/**
 * DashboardCharts — sezioni grafiche della Dashboard:
 * RevenueTargetsRow (revenue + target conversione), TrendChartCard (area chart),
 * FunnelCard (barre conversione), StageBarProfileRow (bar chart + profili),
 * SourceSplitCard (split sorgenti traffico lead · Fase 62).
 * ★ Fase 59.P2 — estratto da DashboardPage.jsx. Comportamento e testid invariati.
 */
import React from 'react';
import { useTranslation } from 'react-i18next';
import { Card, CardContent } from '../ui/card';
import {
  Euro, CheckCircle2, AlertCircle, Target, Activity, Share2,
} from 'lucide-react';
import {
  ResponsiveContainer, Tooltip, XAxis, YAxis, CartesianGrid,
  BarChart, Bar, Cell, Area, AreaChart,
} from 'recharts';
import { CHART_TICK_STYLE, BAR_RADIUS, fmtEur } from './dashboardUtils';

// ── ★ Fase 62 — Split sorgenti traffico (by_source) ────────────────────────
// Palette coerente con i brand di origine (Meta blu, TikTok nero/rosa, Google
// verde, Manuale emerald scuro, Modulo 2 oro, CSV grigio, API viola).
const SOURCE_META = {
  meta:    { label: 'Meta Ads',   color: '#1877F2' },
  tiktok:  { label: 'TikTok Ads', color: '#111827' },
  google:  { label: 'Google Ads', color: '#34A853' },
  manual:  { label: 'Manuale',    color: '#0C3C2D' },
  modulo2: { label: 'Modulo 2',   color: '#D6B25E' },
  csv:     { label: 'CSV import', color: '#6B7280' },
  api:     { label: 'API',        color: '#7C3AED' },
};

export function SourceSplitCard({ bySource }) {
  const { t } = useTranslation();
  const entries = Object.entries(bySource || {});
  const total = entries.reduce((acc, [, v]) => acc + (Number(v) || 0), 0);
  const rows = entries
    .map(([key, v]) => ({
      key,
      count: Number(v) || 0,
      label: SOURCE_META[key]?.label || key,
      color: SOURCE_META[key]?.color || '#64748B',
    }))
    .sort((a, b) => b.count - a.count);
  return (
    <Card className="border-border" data-testid="source-split-card">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">
              {t('dashboard.sourceSplit.eyebrow', 'Sorgenti traffico')}
            </div>
            <div className="font-medium">
              {t('dashboard.sourceSplit.title', 'Split lead per canale di provenienza')}
            </div>
            <div className="text-[11px] text-muted-foreground mt-0.5" data-testid="source-split-total">
              {t('dashboard.sourceSplit.total', 'Totale nel periodo')}: <b>{total}</b>
            </div>
          </div>
          <Share2 className="w-4 h-4 text-muted-foreground" />
        </div>
        {total === 0 ? (
          <div className="text-[12px] text-muted-foreground italic py-6 text-center" data-testid="source-split-empty">
            {t('dashboard.sourceSplit.empty', 'Nessun lead nel periodo selezionato.')}
          </div>
        ) : (
          <>
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={rows} layout="vertical" margin={{ left: 0, right: 20 }}>
                  <CartesianGrid stroke="#EEF2F0" />
                  <XAxis type="number" tick={CHART_TICK_STYLE} allowDecimals={false} domain={[0, 'auto']} />
                  <YAxis type="category" dataKey="label" width={100} tick={CHART_TICK_STYLE} />
                  <Tooltip />
                  <Bar dataKey="count" radius={BAR_RADIUS}>
                    {rows.map((r) => (
                      <Cell key={r.key} fill={r.color} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="mt-3 flex flex-wrap gap-x-4 gap-y-1 text-[11px] text-muted-foreground">
              {rows.map((r) => {
                const pct = total > 0 ? ((r.count / total) * 100).toFixed(1) : '0.0';
                return (
                  <div key={r.key} className="inline-flex items-center gap-1.5" data-testid={`source-split-legend-${r.key}`}>
                    <span className="inline-block w-2.5 h-2.5 rounded-sm" style={{ background: r.color }} />
                    <span>{r.label}</span>
                    <span className="tabular-nums text-foreground">{r.count}</span>
                    <span className="tabular-nums">({pct}%)</span>
                  </div>
                );
              })}
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}

export function RevenueTargetsRow({ kpi }) {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
      <Card className="border-emerald-200 bg-gradient-to-br from-emerald-50 to-white" data-testid="revenue-tile">
        <CardContent className="p-4">
          <div className="flex items-start justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-emerald-700">Revenue chiusi</div>
              <div className="font-serif text-3xl mt-1 text-emerald-900">{fmtEur(kpi?.totals?.revenue_closed_eur)}</div>
              <div className="text-[11px] text-muted-foreground mt-1">{kpi?.totals?.reached_chiuso || 0} lead chiusi</div>
            </div>
            <div className="w-9 h-9 rounded-lg grid place-items-center bg-emerald-100">
              <Euro className="w-4 h-4 text-emerald-700" />
            </div>
          </div>
        </CardContent>
      </Card>

      {(kpi?.funnel || []).filter((f) => f.target_pct).map((f) => {
        const onTarget = f.pct >= f.target_pct;
        return (
          <Card
            key={f.key}
            className={`border-2 ${onTarget ? 'border-emerald-300 bg-emerald-50/40' : 'border-amber-300 bg-amber-50/40'}`}
            data-testid={`conversion-target-${f.key}`}
          >
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{f.label}</div>
                  <div className="flex items-baseline gap-2 mt-1">
                    <div className={`font-serif text-3xl ${onTarget ? 'text-emerald-700' : 'text-amber-700'}`}>
                      {f.pct.toFixed(1)}%
                    </div>
                    <div className="text-xs text-muted-foreground">/ {f.target_pct.toFixed(0)}% target</div>
                  </div>
                  <div className="text-[11px] mt-1">
                    {onTarget ? (
                      <span className="inline-flex items-center text-emerald-700">
                        <CheckCircle2 className="w-3 h-3 mr-1" /> In target
                      </span>
                    ) : (
                      <span className="inline-flex items-center text-amber-700">
                        <AlertCircle className="w-3 h-3 mr-1" /> Sotto target
                      </span>
                    )}
                    <span className="ml-2 text-muted-foreground">{f.num}/{f.den}</span>
                  </div>
                </div>
                <Target className={`w-5 h-5 ${onTarget ? 'text-emerald-600' : 'text-amber-600'}`} />
              </div>
            </CardContent>
          </Card>
        );
      })}
    </div>
  );
}

export function TrendChartCard({ series, granularity }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Trend</div>
            <div className="font-medium">
              Andamento lead — vista {granularity === 'daily' ? 'giornaliera' : granularity === 'weekly' ? 'settimanale' : 'mensile'}
            </div>
          </div>
          <Activity className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="h-64">
          {series && (
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={series}>
                <CartesianGrid stroke="#EEF2F0" />
                <XAxis dataKey="label" tick={CHART_TICK_STYLE} />
                <YAxis tick={CHART_TICK_STYLE} allowDecimals={false} domain={[0, 'auto']} />
                <Tooltip />
                <Area type="monotone" dataKey="leads" stroke="#0C3C2D" fill="#0C3C2D" fillOpacity={0.15} strokeWidth={2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </div>
      </CardContent>
    </Card>
  );
}

export function FunnelCard({ funnel }) {
  return (
    <Card className="border-border">
      <CardContent className="p-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Funnel</div>
            <div className="font-medium">Tasso di conversione tra stage</div>
          </div>
          <Activity className="w-4 h-4 text-muted-foreground" />
        </div>
        <div className="space-y-2">
          {(funnel || []).map((f) => {
            const target = f.target_pct;
            const onTarget = target ? f.pct >= target : null;
            return (
              <div key={f.key} className="flex items-center gap-3" data-testid={`funnel-row-${f.key}`}>
                <div className="text-xs w-56 truncate">{f.label}</div>
                <div className="flex-1 h-5 rounded-full bg-muted/40 relative overflow-hidden">
                  <div
                    className={`h-full ${onTarget === true ? 'bg-emerald-500' : onTarget === false ? 'bg-amber-500' : 'bg-[hsl(var(--primary))]'}`}
                    style={{ width: `${Math.min(f.pct, 100)}%` }}
                  />
                  {target && (
                    <div
                      className="absolute top-0 bottom-0 border-l-2 border-dashed border-rose-400"
                      style={{ left: `${Math.min(target, 100)}%` }}
                      title={`Target ${target}%`}
                    />
                  )}
                </div>
                <div className="text-xs font-mono w-24 text-right tabular-nums">{f.pct.toFixed(1)}%</div>
                <div className="text-[10px] text-muted-foreground w-20 text-right tabular-nums">{f.num}/{f.den}</div>
              </div>
            );
          })}
        </div>
      </CardContent>
    </Card>
  );
}

export function StageBarProfileRow({ stageBars, kpi, profileTotal }) {
  const { t } = useTranslation();
  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-2 border-border">
        <CardContent className="p-4">
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Pipeline</div>
            <div className="font-medium">{t('dashboard.leadsByStageShort', 'Lead per stage')}</div>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={stageBars}>
                <CartesianGrid stroke="#EEF2F0" />
                <XAxis dataKey="stage" tick={CHART_TICK_STYLE} interval={0} angle={-25} textAnchor="end" height={60} />
                <YAxis tick={CHART_TICK_STYLE} allowDecimals={false} domain={[0, 'auto']} />
                <Tooltip />
                <Bar dataKey="count" radius={BAR_RADIUS}>
                  {stageBars.map((entry, idx) => (
                    <Cell key={`cell-${idx}`} fill={entry.color} />
                  ))}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border">
        <CardContent className="p-4">
          <div className="mb-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Profili</div>
            <div className="font-medium">Asset interest</div>
          </div>
          <div className="space-y-2">
            {[
              { key: 'equity', label: '💰 Equity Share', color: 'bg-emerald-500' },
              { key: 'real_estate', label: '🏠 Real Estate', color: 'bg-amber-500' },
              { key: 'both', label: '🌐 Equity + RE', color: 'bg-violet-500' },
            ].map((p) => {
              const v = kpi?.profile_counts?.[p.key] || 0;
              const pct = profileTotal > 0 ? (v / profileTotal) * 100 : 0;
              return (
                <div key={p.key} className="space-y-1" data-testid={`profile-row-${p.key}`}>
                  <div className="flex items-center justify-between text-xs">
                    <span>{p.label}</span>
                    <span className="font-mono tabular-nums text-muted-foreground">{v}</span>
                  </div>
                  <div className="h-2 rounded-full bg-muted/40 overflow-hidden">
                    <div className={`h-full ${p.color}`} style={{ width: `${pct}%` }} />
                  </div>
                </div>
              );
            })}
            {profileTotal === 0 && (
              <div className="text-[11px] text-muted-foreground italic py-2">{t('dashboard.noProfileSelected', 'Nessun profilo selezionato ancora.')}</div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
